import { useState, useEffect, useCallback } from 'react';
import {
    JobDetailBonusStatusTimelineResult,
    BonusStatusTimelineEvent,
    BonusStatusSummary,
    BonusStatusSummaryApi,
    BonusStatusTimelineEventApi
} from '../providers/types';
import httpClient from '../utils/httpClient';

interface UseBonusStatusTimelineResult {
    data: JobDetailBonusStatusTimelineResult | null;
    isLoading: boolean;
    error: string | null;
    refetch: () => void;
}

interface JobDetailsBonusStatusTimelineApiResponse {
    jobId: number;
    bonusStatusSummaries: BonusStatusSummaryApi[];
    bonusStatusTimelineEvents: BonusStatusTimelineEventApi[];
}

const parseISODate = (dateString?: string | null): Date | undefined => {
    if (!dateString) return undefined;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? undefined : date;
};

const transformBonusStatusSummary = (
    summary: BonusStatusSummaryApi
): BonusStatusSummary => ({
    id: summary.id,
    jobId: summary.jobId,
    jobNumber: summary.jobNumber,
    projectNumber: summary.projectNumber,
    bonusTypeId: summary.bonusTypeId,
    bonusTypeName: summary.bonusTypeName,
    currentStatus: summary.currentStatus,
    lastUpdated: parseISODate(summary.lastUpdated) ?? new Date(),
    lastUpdatedBy: summary.lastUpdatedBy,
    pmReviewedOn: parseISODate(summary.pmReviewedOn),
    pmReviewer: summary.pmReviewer ?? undefined,
    opsReviewedOn: parseISODate(summary.opsReviewedOn),
    opsReviewer: summary.opsReviewer ?? undefined,
    spmReviewedOn: parseISODate(summary.spmReviewedOn),
    spmReviewer: summary.spmReviewer ?? undefined,
    dmReviewedOn: parseISODate(summary.dmReviewedOn),
    dmReviewer: summary.dmReviewer ?? undefined,
    payrollProcessedOn: parseISODate(summary.payrollProcessedOn),
    payrollProcessor: summary.payrollProcessor ?? undefined,
    payrollPaidOn: parseISODate(summary.payrollPaidOn),
    payrollPaidBy: summary.payrollPaidBy ?? undefined,
    bim360Upload: summary.bim360Upload,
    bim360UploadOn: parseISODate(summary.bim360UploadOn),
    bim360UploadIssue: summary.bim360UploadIssue ?? undefined,
    createdOn: parseISODate(summary.createdOn) ?? new Date(),
    createdBy: summary.createdBy,
});

const transformTimelineEvent = (
    event: BonusStatusTimelineEventApi
): BonusStatusTimelineEvent => ({
    eventTimestamp: new Date(event.eventTimestamp),
    eventType: event.eventType,
    bonusTypeId: event.bonusTypeId,
    bonusTypeName: event.bonusTypeName,
    updatedByUser: event.updatedByUser,
    roleName: event.roleName,
    previousStatus: event.previousStatus ?? undefined,
    currentStatus: event.currentStatus ?? undefined,
    commentText: event.commentText ?? undefined,
    action: event.action ?? undefined,
    statusHistoryId: event.statusHistoryId ?? undefined,
    commentId: event.commentId ?? undefined,
    fileAttachmentId: event.fileAttachmentId ?? undefined,
    fileName: event.fileName ?? undefined,
    attachedFile: event.attachedFile ?? undefined,
});

const transformApiResponse = (
    apiData: JobDetailsBonusStatusTimelineApiResponse
): JobDetailBonusStatusTimelineResult => {
    return {
        jobId: apiData.jobId,
        bonusStatusSummaries: apiData.bonusStatusSummaries.map(transformBonusStatusSummary),
        bonusStatusTimelineEvents: apiData.bonusStatusTimelineEvents.map(transformTimelineEvent),
    };
};

export const useBonusStatusTimeline = (
    jobId: number | null
): UseBonusStatusTimelineResult => {
    const [data, setData] = useState<JobDetailBonusStatusTimelineResult | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchTimeline = useCallback(async () => {
        if (!jobId) {
            setData(null);
            setIsLoading(false);
            setError(null);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await httpClient.get<JobDetailsBonusStatusTimelineApiResponse>(
                `/api/cba/job-details/bonus-status-timeline/${jobId}`
            );
            console.log('API Response:', response.data); // Debug log

            const result = response.data;

            if (!result || typeof result !== 'object') {
                throw new Error('Invalid response format');
            }

            // Validate that required arrays exist
            if (!Array.isArray(result.bonusStatusSummaries)) {
                console.error('Missing bonusStatusSummaries array:', result);
                throw new Error('API response missing bonusStatusSummaries array');
            }

            if (!Array.isArray(result.bonusStatusTimelineEvents)) {
                console.error('Missing bonusStatusTimelineEvents array:', result);
                throw new Error('API response missing bonusStatusTimelineEvents array');
            }

            const transformedData = transformApiResponse(result);
            setData(transformedData);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
            setError(errorMessage);
            console.error('Error fetching bonus status timeline:', err);
            setData(null);
        } finally {
            setIsLoading(false);
        }
    }, [jobId]);

    useEffect(() => {
        fetchTimeline();
    }, [fetchTimeline]);

    return {
        data,
        isLoading,
        error,
        refetch: fetchTimeline
    };
};